/**
 * Kartvy för ett fordons position (aktuell eller senast sparade).
 * Samma vy som kundtjänst.
 */

import { useEffect, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMap } from 'react-leaflet';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import BaseModal from '@/components/common/BaseModal';
import 'leaflet/dist/leaflet.css';
import '@/utils/leaflet-fix';

export interface VehicleMapPosition {
  vehicleId: number | null;
  registrationNumber: string | null;
  vehicleLabel: string | null;
  lat: number | null;
  lng: number | null;
  timestamp: string | null;
  source: 'realtime' | 'stored' | null;
}

function FitVehicle({ center }: { center: [number, number] }) {
  const map = useMap();
  useEffect(() => {
    const id = window.setTimeout(() => map.invalidateSize(), 80);
    return () => window.clearTimeout(id);
  }, [map]);
  useEffect(() => {
    map.setView(center, 16);
  }, [map, center]);
  return null;
}

export function VehiclePositionMapModal({
  fallbackLabel,
  onClose,
  load,
}: {
  fallbackLabel: string;
  onClose: () => void;
  load: () => Promise<VehicleMapPosition>;
}) {
  const { t } = useTranslation();
  const [data, setData] = useState<VehicleMapPosition | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    void load()
      .then((pos) => {
        if (!cancelled) setData(pos);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const title = data?.vehicleLabel || data?.registrationNumber || fallbackLabel;
  const hasCoords = data != null && data.lat != null && data.lng != null;
  const center: [number, number] | null = hasCoords ? [data.lat as number, data.lng as number] : null;
  const sourceLabel =
    data?.source === 'realtime'
      ? t('booking.vehicleMapSourceLive')
      : data?.source === 'stored'
        ? t('booking.vehicleMapSourceStored')
        : null;
  const updatedAt = data?.timestamp
    ? new Date(data.timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
    : null;

  return (
    <BaseModal
      isOpen
      onClose={onClose}
      title={title}
      subtitle={t('booking.vehicleMapTitle')}
      size="2xl"
      zIndex={100010}
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-textDefault">
          {sourceLabel && (
            <span className="inline-flex items-center gap-1 font-medium">
              <MapPinIcon className="h-4 w-4 text-primary" />
              {sourceLabel}
            </span>
          )}
          {updatedAt && (
            <span className="text-textSecondary">
              {t('booking.vehicleMapUpdatedAt')}: {updatedAt}
            </span>
          )}
        </div>
        {loading ? (
          <p className="rounded-lg border border-border bg-bgBase px-3 py-12 text-center text-sm text-textSecondary">
            {t('common.loading')}
          </p>
        ) : error ? (
          <p className="rounded-lg border border-border bg-bgBase px-3 py-12 text-center text-sm text-textSecondary">
            {t('booking.vehicleMapLoadError')}
          </p>
        ) : !data?.vehicleId ? (
          <p className="rounded-lg border border-border bg-bgBase px-3 py-12 text-center text-sm text-textSecondary">
            {t('booking.noVehicleAssigned')}
          </p>
        ) : center ? (
          <div className="h-[min(480px,60vh)] w-full overflow-hidden rounded-lg border border-border">
            <MapContainer center={center} zoom={16} className="z-0 h-full w-full" scrollWheelZoom>
              <TileLayer
                url="https://app.deque.se/api/map/tiles/{z}/{x}/{y}.png?v=3"
                attribution="&copy; OpenStreetMap"
              />
              <Marker position={center} />
              <FitVehicle center={center} />
            </MapContainer>
          </div>
        ) : (
          <p className="rounded-lg border border-border bg-bgBase px-3 py-12 text-center text-sm text-textSecondary">
            {t('booking.vehicleMapNoPosition')}
          </p>
        )}
      </div>
    </BaseModal>
  );
}
